import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Expert } from './expert.type';
import { ThreeButtonWrapper } from '../../connectivity/threeButtons/ThreeButtonWrapper';
import { Crown, Sparkles, CheckCircle2, Flame, Loader2, UserPlus, Check } from 'lucide-react'; 

import { useExpertReviews } from '../../hooks/expertReviewHooks/useExpertReviews'; 

// 🟢 1. IMPORT FOLLOWER HOOK
import { useFollower } from '../../hooks/expertFollowerHooks/useFollower';

// 🟢 2. IMPORT LIVE STREAM HOOK DIRECTLY HERE
import { useLiveStreamStatus } from '../../hooks/liveStreamHooks/useLiveStreamStatus'; 
import { optimizeCloudinaryImage } from './imageHelper/ImageCompressorHelper';

// Notice: Removed `isLive` from the props because the card handles it internally now
export const ExpertCard = ({ data, liveStatus }: { data: Expert, liveStatus?: string }) => {
  const navigate = useNavigate();

 const localUserCode = localStorage.getItem('user_code');

const currencySymbol =
  sessionStorage.getItem('guest_country') === 'IN' ? '₹' : '$';


  const storedOffer = localStorage.getItem("has_used_first_offer");
const hasUsedOffer = storedOffer === "true"; // logged-in users only

const isAstrology = data.category?.toLowerCase() === 'astrology';

// 👇 IMPORTANT CONDITION
const shouldShowFree = isAstrology && (!storedOffer || hasUsedOffer === false);


  // =========================================================================
  // 🟢 FETCH LIVE STREAM STATUS INDEPENDENTLY
  // =========================================================================
  const liveExpertsMap = useLiveStreamStatus();
  const isLive = liveExpertsMap[data.expertCode] || false;



  // =========================================================================
  // 🟢 FETCH LIVE REVIEWS & RATING
  // =========================================================================
  const { reviews, ratingStats, isLoadingReviews } = useExpertReviews(data.expertCode);

  const actualReviewsCount = ratingStats?.reviewsCount > 0 ? ratingStats.reviewsCount : (reviews?.length || 0);
  let actualRating = ratingStats?.rating > 0 ? ratingStats.rating : 0;
  
  if (actualRating === 0 && actualReviewsCount > 0) {
    const sum = reviews.reduce((acc, r) => acc + (Number(r.rating) || 5), 0);
    actualRating = Number((sum / actualReviewsCount).toFixed(1));
  }

  let displayRating: ReactNode; 
  
  if (isLoadingReviews) {
    displayRating = <Loader2 className="w-3 h-3 animate-spin text-yellow-600" />;
  } else if (actualReviewsCount > 0) {
    displayRating = actualRating;
  } else {
    displayRating = "No ratings"; 
  }

  // =========================================================================
  // 🟢 FOLLOWER LOGIC
  // =========================================================================
  const { isFollowing, handleToggleFollow } = useFollower(data.expertCode, localUserCode);

  // =========================================================================
  // 🚀 STATUS LOGIC
  // =========================================================================
const hasAnyService =
  data.isAvailableChat ||
  data.isAvailableCall ||
  data.isAvailableVideoCall;

let dotColor = 'bg-gray-400';

if (liveStatus === 'busy') {
  dotColor = 'bg-yellow-500';
} else if (liveStatus === 'available' && hasAnyService) {
  dotColor = 'bg-emerald-500 animate-pulse';
}

  return (
    <div 
      onClick={() => navigate(`/expert/${data.expertCode}`)}
      className={`cursor-pointer rounded-[24px] p-5 transition-all duration-300 relative group flex flex-col hover:shadow-xl h-full
        ${data.isSupreme 
          ? 'bg-gradient-to-br from-amber-50 via-white to-amber-100 border-2 border-amber-200 shadow-lg shadow-amber-100/50' 
          : 'bg-white border border-gray-100 shadow-sm'
        }
      `}
    >
      
      {/* BADGES */}
      <div className="absolute top-0 left-0 z-10 flex flex-col gap-1 items-start">
        {data.isSupreme && (
          <div className="bg-gradient-to-r from-yellow-400 to-amber-500 text-amber-950 border-b border-r border-amber-400 text-[10px] font-bold px-3 py-1 rounded-br-xl rounded-tl-[24px] flex items-center gap-1 shadow-md">
            <Crown className="w-3 h-3" /> SUPREME
          </div>
        )}
        {data.isFeatured && !data.isSupreme && (
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-br-xl rounded-tl-[24px] flex items-center gap-1 shadow-md">
            <Sparkles className="w-3 h-3" /> FEATURED
          </div>
        )}
        {data.isNew && (
          <div className={`bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[10px] font-bold px-3 py-1 flex items-center gap-1 shadow-md  
            ${(data.isSupreme || data.isFeatured) ? 'rounded-r-xl' : 'rounded-br-xl rounded-tl-[24px]'}`
          }>
            <Flame className="w-3 h-3" /> NEW
          </div>
        )}
      </div>

      {/* LIVE BADGE AT TOP RIGHT */}
      {isLive && (
        <div className="absolute top-3 right-3 z-10">
          <span className="flex items-center gap-1 bg-red-500 text-white text-[9px] font-black px-2 py-1 rounded-full shadow-md animate-pulse tracking-wide border-2 border-white">
            <span className="w-1.5 h-1.5 bg-white rounded-full"></span>LIVE
          </span>
        </div>
      )}

      <div className="flex justify-between items-start mb-3 mt-3">
        <div className="relative">
          <div className={`w-[80px] h-[80px] rounded-full p-1 border-2 transition-all ${isLive ? 'border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.3)]' : (data.isVerified ? 'border-blue-500' : 'border-gray-200')}`}>
            <img src={optimizeCloudinaryImage(data.image, 160, 160)} alt={data.name}   loading="lazy"
  decoding="async" className="w-full h-full object-cover rounded-full" />
          </div>
          {!isLive && (
            <div className={`absolute bottom-1 right-1 w-4 h-4 border-2 border-white rounded-full ${dotColor}`}></div>
          )}
        </div>
        
        <div className="flex flex-col items-end gap-1.5">
          {data.isVerified && (
            <span className="bg-blue-50 text-blue-600 text-[10px] font-bold px-2 py-0.5 rounded-md flex items-center gap-1 border border-blue-100">
              <CheckCircle2 className="w-3 h-3" /> VERIFIED
            </span>
          )}
          
          <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-lg border border-yellow-100 min-w-[40px] justify-center">
            <span className="text-sm font-bold text-yellow-700 flex items-center justify-center">
              {displayRating}
            </span>
            {displayRating !== "No ratings" && !isLoadingReviews && (
              <svg className="w-3 h-3 text-yellow-500 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
            )}
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation(); 
              handleToggleFollow();
            }}
            className={`flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md transition-colors border ${
              isFollowing 
                ? 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100' 
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {isFollowing ? (
              <>
                <Check className="w-3 h-3" /> FOLLOWING
              </>
            ) : (
              <>
                <UserPlus className="w-3 h-3" /> FOLLOW
              </>
            )}
          </button>
        </div>
      </div>

      {/* 🟢 FIXED CONTENT LAYER: Built defensively for consistent vertical alignment */}
      <div className="flex-1 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate">{data.name}</h3>
            {liveStatus === 'busy' && !isLive && (
              <span className="bg-yellow-50 text-yellow-700 border border-yellow-200 text-[9px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1 shrink-0">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse"></span> Busy
              </span>
            )}
          </div>
          
          {/* 🟢 FIXED SPECIALTY CONTAINER: Swapped standard height for min-height to ensure native line clamps map perfectly */}
          <p className="text-gray-500 text-xs font-medium tracking-wide mt-1 font-['Poppins'] leading-5 md:line-clamp-4 md:min-h-[80px] overflow-hidden">
            {data.specialty}
          </p>
        </div>

        <div className="flex items-center gap-2 mt-3 text-gray-400 text-xs pt-1">
          <span>{data.experience}</span>
          <span>•</span>
          <span className="truncate max-w-[100px]">{data.languages}</span>
        </div>
      </div>

<div className="mt-4 mb-4">
  {shouldShowFree ? (
    <>
      <span className="text-green-600 font-bold text-lg">FREE</span>{" "}
      <span className="line-through text-gray-400 text-sm">
        {currencySymbol}{data.price}
      </span>
    </>
  ) : (
    <span className="text-lg font-extrabold text-gray-900">
      {currencySymbol}{data.price}
    </span>
  )}

  {data.category?.toLowerCase() !== 'doctor' && (
    <span className="text-xs text-gray-400 font-normal"> / min</span>
  )}
</div>
      <div onClick={(e) => e.stopPropagation()}>
         <ThreeButtonWrapper 
           variant={data.isSupreme ? 'supreme' : 'default'}
           isChatAvailable={data.isAvailableChat}
           isCallAvailable={data.isAvailableCall}
           isVideoAvailable={data.isAvailableVideoCall}
           expertCode={data.expertCode}
           expertName={data.name}
           expertCategory={data.category}
           categoryId={data.categoryId}
           expertSubCategories={data.specialty}
           expertPhoto={data.image}
           liveStatus={liveStatus}
           isLive={isLive}
         />
      </div>
      
    </div>
  );
};